import React, { useState, useEffect } from 'react';
import Header from '../components/menu/header';
import Footer from '../components/menu/footer';
import './regolamento.css';

const Regolamento = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Gestione scroll e evidenziazione sezione attiva
  useEffect(() => {
    const handleScroll = () => {
      // Mostra/nascondi bottone scroll to top
      setShowScrollTop(window.pageYOffset > 300);

      // Trova la sezione corrente con priorità ai sottocapitoli
      const sections = document.querySelectorAll('.regolamento-section[id]');
      let currentSection = '';
      let bestMatch = null;
      let bestDistance = Infinity;

      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top - 100);
        
        // Solo sezioni visibili nella parte superiore dello schermo
        if (rect.top <= 150 && rect.bottom >= 50 && distance < bestDistance) {
          bestDistance = distance;
          bestMatch = section.id;
        }
      });

      // Se abbiamo trovato una sezione, usala
      if (bestMatch) {
        currentSection = bestMatch;
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Chiamata iniziale

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll del menu per mantenere l'elemento attivo visibile
  useEffect(() => {
    if (activeSection) {
      // Aggiungi un piccolo delay per evitare flickering
      const timer = setTimeout(() => {
        const activeLink = document.querySelector(`.regolamento-toc a[href="#${activeSection}"]`);
        const tocContainer = document.querySelector('.regolamento-toc');
        
        if (activeLink && tocContainer) {
          const linkRect = activeLink.getBoundingClientRect();
          const containerRect = tocContainer.getBoundingClientRect();
          
          // Calcola la posizione del link all'interno del container scrollabile
          const linkOffsetTop = activeLink.offsetTop;
          const containerScrollTop = tocContainer.scrollTop;
          const containerHeight = containerRect.height;
          
          // Posizione relativa del link rispetto alla vista corrente del container
          const linkTopInView = linkOffsetTop - containerScrollTop;
          const linkBottomInView = linkTopInView + linkRect.height;
          
          // Margini per determinare quando scrollare
          const topMargin = 40;
          const bottomMargin = 40;
          
          // Verifica se il link è fuori dalla vista
          const isAboveView = linkTopInView < topMargin;
          const isBelowView = linkBottomInView > containerHeight - bottomMargin;
          
          // Solo se il link è fuori dalla vista, scrolla per renderlo visibile
          if (isAboveView || isBelowView) {
            let targetScrollTop;
            
            if (isAboveView) {
              // Se è sopra, portalo in alto con un margine
              targetScrollTop = linkOffsetTop - topMargin;
            } else {
              // Se è sotto, portalo in basso con un margine
              targetScrollTop = linkOffsetTop - containerHeight + linkRect.height + bottomMargin;
            }
            
            // Assicurati che non vada sotto 0
            targetScrollTop = Math.max(0, targetScrollTop);
            
            // Evita scroll se la differenza è minima
            if (Math.abs(containerScrollTop - targetScrollTop) > 10) {
              tocContainer.scrollTo({
                top: targetScrollTop,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activeSection]);

  // Funzione per scroll smooth
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Funzione per tornare in cima
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleDownloadPDF = (filename) => {
    const link = document.createElement('a');
    link.href = `/documents/${filename}`;
    link.download = filename;
    link.target = '_blank'; // Fallback: apre in nuova tab
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dati per l'indice
  const tocData = [
    {
      id: 'premessa',
      title: 'PREMESSA',
      children: []
    },
    {
      id: 'indicazioni-generali',
      title: '1 - INDICAZIONI GENERALI',
      children: [
        { id: 'il-gioco', title: '1.1 - Il gioco' },
        { id: 'il-campo-da-gioco', title: '1.2 - Il campo da gioco' },
        { id: 'composizione-delle-squadre', title: '1.3 - Composizione delle squadre' },
        { id: 'lautorità-ed-i-poteri-dellarbitro', title: '1.4 - L\'autorità ed i poteri dell\'arbitro' },
        { id: 'refertisti', title: '1.5 - Refertisti' },
        { id: 'il-capitano-della-squadra', title: '1.6 - Il capitano della squadra' },
        { id: 'abbigliamento', title: '1.7 - Abbigliamento' },
        { id: 'lo-scalpo', title: '1.8 - Lo scalpo' },
        { id: 'tempi-regolamentari', title: '1.9 - Tempi regolamentari' },
        { id: 'tempi-supplementari', title: '1.10 - Tempi supplementari' },
        { id: 'time-out', title: '1.11 - Time-out' },
        { id: 'tempo-di-recupero', title: '1.12 - Tempo di recupero' },
        { id: 'sostituzione-dei-giocatori-nel-corso-dellincontro', title: '1.13 - Sostituzione dei giocatori nel corso dell\'incontro' }
      ]
    },
    {
      id: 'svolgimento-del-gioco',
      title: '2 - SVOLGIMENTO DEL GIOCO',
      children: [
        { id: 'cerchio-di-inizio-incontro', title: '2.1 - Cerchio di inizio incontro' },
        { id: 'inizio-del-gioco', title: '2.2 - Inizio del gioco' },
        { id: 'il-possesso-della-palla', title: '2.3 - Il possesso della palla' },
        { id: 'palla-contesa', title: '2.4 - Palla contesa' },
        { id: 'scalpo-vuoto-o-a-vuoto-o-non-buono', title: '2.5 - Scalpo "vuoto" o "a vuoto" o "non buono"' },
        { id: 'vuoti-di-squadra', title: '2.6 - Vuoti di squadra' },
        { id: 'scalpo-buono-o-pieno', title: '2.7 - Scalpo "buono" o "pieno"' },
        { id: 'meta', title: '2.8 - Meta' },
        { id: 'ripresa-del-gioco-dopo-una-meta', title: '2.9 - Ripresa del gioco dopo una meta' },
        { id: 'meta-tecnica', title: '2.10 - Meta tecnica' },
        { id: 'il-portiere', title: '2.11 - Il portiere' },
        { id: 'rimessa-dopo-fallo-o-scalpo', title: '2.12 - Rimessa dopo fallo o scalpo' }
      ]
    },
    {
      id: 'falli-e-sanzioni',
      title: '3 - FALLI E SANZIONI',
      children: [
        { id: 'fallo-laterale-e-fallo-di-fondo', title: '3.1 - Fallo laterale e fallo di fondo' },
        { id: 'fallo-di-corpo', title: '3.2 - Fallo di corpo' },
        { id: 'fallo-di-sfondamento', title: '3.3 - Fallo di sfondamento' },
        { id: 'fallo-di-blocco', title: '3.4 - Fallo di blocco' },
        { id: 'fallo-di-piede', title: '3.5 - Fallo di piede' },
        { id: 'fallo-di-palleggio', title: '3.6 - Fallo di palleggio' },
        { id: 'fallo-di-scalpo-preparato-o-manina-o-mano-sullo-scalpo', title: '3.7 - Fallo di scalpo preparato o "manina" o "mano sullo scalpo"' },
        { id: 'fallo-di-palo', title: '3.8 - Fallo di palo' },
        { id: 'fallo-di-protezione', title: '3.9 - Fallo di protezione' },
        { id: 'fallo-di-vestiario', title: '3.10 - Fallo di vestiario' },
        { id: 'fallo-di-posizione-in-attacco', title: '3.11 - Fallo di posizione in attacco' },
        { id: 'fallo-di-posizione-in-difesa', title: '3.12 - Fallo di posizione in difesa' },
        { id: 'fallo-di-melina', title: '3.13 - Fallo di melina' },
        { id: 'fallo-campo', title: '3.14 - Fallo campo' },
        { id: 'regola-del-vantaggio', title: '3.15 - Regola del vantaggio' },
        { id: 'uscita-dal-fondo-di-un-giocatore', title: '3.16 - Uscita dal fondo di un giocatore' },
        { id: 'fallo-chiara-occasione-da-meta', title: '3.17 - Fallo "chiara occasione da meta"' },
        { id: 'falli-di-squadra', title: '3.18 - Falli di squadra' },
        { id: 'infrazioni-sanzionabili-con-uno-scalpo-a-vuoto', title: '3.19 - Infrazioni sanzionabili con uno scalpo a vuoto' },
        { id: 'infrazioni-sanzionabili-con-due-scalpi-a-vuoto', title: '3.20 - Infrazioni sanzionabili con due scalpi a vuoto' },
        { id: 'infrazioni-passibili-di-ammonizione-giallo', title: '3.21 - Infrazioni passibili di ammonizione (giallo)' },
        { id: 'infrazioni-passibili-di-espulsione-rosso', title: '3.22 - Infrazioni passibili di espulsione (rosso)' },
        { id: 'infrazioni-passibili-di-espulsione-nero', title: '3.23 - Infrazioni passibili di espulsione (nero)' }
      ]
    }
  ];

  // Componente per renderizzare l'indice
  const TableOfContents = () => (
    <nav className="regolamento-toc">
      <ul>
        {tocData.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              className={activeSection === section.id ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
            >
              {section.title}
            </a>
            {section.children.length > 0 && (
              <ul>
                {section.children.map((subsection) => (
                  <li key={subsection.id}>
                    <a
                      href={`#${subsection.id}`}
                      className={activeSection === subsection.id ? 'active' : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(subsection.id);
                      }}
                    >
                      {subsection.title}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <div className="regolamento-page">
      <Header />

      {/* Header della pagina */}
      <section className="regolamento-hero">
        <div className="regolamento-hero-content">
          <h1><div className="lc-rhino-icon"></div>REGOLAMENTO SCOUTBALL</h1>
          <button onClick={_ => handleDownloadPDF('regolamento25riassunto.pdf')} className="regolamento-download-btn">
            <i className="fas fa-download"></i>
            regolamento semplificato
          </button>
          <button onClick={_ => handleDownloadPDF('regolamento25completo.pdf')} className="regolamento-download-btn">
            <i className="fas fa-download"></i>
            regolamento completo
          </button>
          {/* <button onClick={_ => handleDownloadPDF('regolamento25arbitri.pdf')} className="regolamento-download-btn">
            <i className="fas fa-download"></i>
            regolamento per arbitri
          </button> */}
        </div>
      </section>

      {/* Container principale */}
      <div className="regolamento-container">
        {/* Sidebar con indice */}
        <aside className="regolamento-sidebar">
          <div className="regolamento-toc-header">
            <i className="fas fa-list"></i>
            INDICE
          </div>
          <TableOfContents />
        </aside>

        {/* Contenuto principale */}
        <main className="regolamento-content">
          {/* Premessa */}
          <section id="premessa" className="regolamento-section">
            <h1>PREMESSA</h1>
            <div className="regolamento-premessa">
              Lo Scoutball è un Gioco Scout, pertanto la prima regola fondamentale è il rispetto di tutti quei valori che ci distinguono, codificati nella Legge e nella Promessa Scout.
            </div>
          </section>

          {/* 1 - INDICAZIONI GENERALI */}
          <section id="indicazioni-generali" className="regolamento-section">
            <h1>1 - INDICAZIONI GENERALI</h1>

            <section id="il-gioco" className="regolamento-section">
              <h2>1.1 - Il gioco</h2>
              <p>Lo Scoutball viene giocato da due squadre di sette giocatori ciascuna. Lo scopo del gioco è realizzare più mete della squadra avversaria.</p>
            </section>

            <section id="il-campo-da-gioco" className="regolamento-section">
              <h2>1.2 - Il campo da gioco</h2>
              <p>Il campo di gioco è un rettangolo di 30 x 18 m con fondo erboso, delimitato da nastri. Sulle righe laterali sono segnalate in modo visibile la metà campo e le aree (4m dal fondo di entrambi i lati).</p>
              <p>Le porte sono larghe 4 m, sono situate al centro dei lati corti e delimitate da due paletti piantati a terra alti almeno 1.5 m, liberi di cadere se urtati con forza.</p>
              <p>La palla è sferica, di nylon vulcanizzato di taglia 0 (Ø 13-15 cm)</p>
            </section>

            <section id="composizione-delle-squadre" className="regolamento-section">
              <h2>1.3 - Composizione delle squadre</h2>
              <p>In campo ogni squadra schiera 7 giocatori, di cui almeno 2 per sesso, e da eventuali riserve. Non c'è limite ai giocatori iscrivibili per squadra.</p>
            </section>

            <section id="lautorità-ed-i-poteri-dellarbitro" className="regolamento-section">
              <h2>1.4 - L'autorità ed i poteri dell'arbitro</h2>
              <p>Per ogni partita è designato un primo arbitro. La sua autorità, che gli è conferita dal presente regolamento, inizia nel momento in cui arriva sul campo e cessa quando se ne è andato definitivamente.</p>
              <p>È compito dell'arbitro:</p>
              <ul>
                <li>applicare e far rispettare il presente regolamento;</li>
                <li>verificare la correttezza e l'adeguatezza dell'abbigliamento da gioco;</li>
                <li>tenere il tempo di gioco;</li>
                <li>assicurarsi che non entrino nel rettangolo di gioco persone non autorizzate;</li>
                <li>interrompere il gioco se ritiene che un giocatore sia gravemente infortunato ed assicurarsi che venga soccorso adeguatamente;</li>
              </ul>
              <p>È consigliabile un secondo arbitro, le cui prerogative sono:</p>
              <ul>
                <li>assistere il primo arbitro nello svolgimento dell'incontro;</li>
                <li>se precedentemente concordato con il primo arbitro, ha facoltà di interrompere il gioco per qualsiasi infrazione al presente regolamento;</li>
              </ul>
              <p>Le decisioni dell'arbitro sono inappellabili e il suo giudizio è insindacabile. L'arbitro ha la facoltà di sanzionare un giocatore che ha commesso una scorrettezza proporzionalmente alla gravità e all'antisportività del fallo commesso, anche qualora questo non stia giocando.</p>
            </section>

            <section id="refertisti" className="regolamento-section">
              <h2>1.5 - Refertisti</h2>
              <p>I refertisti, uno per ciascuna squadra, hanno il compito di compilare fedelmente il referto dell'incontro, seguendo le indicazioni fornite dall'arbitro. Durante il cerchio iniziale l'arbitro si sincererà della presenza dei refertisti.</p>
            </section>

            <section id="il-capitano-della-squadra" className="regolamento-section">
              <h2>1.6 - Il capitano della squadra</h2>
              <p>Il capitano della squadra è responsabile della condotta dei giocatori della propria squadra e rappresentante della squadra presso l'arbitro. Solo il capitano ha diritto di rivolgersi all'arbitro, a gioco fermo o a fine gara, per esprimere, in forma corretta e rispettosa, riserve o per avere chiarimenti. Non è necessario che il capitano sia sempre sul campo di gioco, potendo relazionarsi con l'arbitro e i giocatori anche dall'esterno di esso. Non è possibile trasferire il ruolo di capitano nel corso dell'incontro, salvo per gravi ragioni e comunque solo previo consenso dell'arbitro.</p>
              <p>È dovere del capitano coadiuvare l'arbitro ai fini del regolare svolgimento dell'incontro e provvedere a reprimere ogni intemperanza dei giocatori della propria squadra e di ogni sostenitore e/o accompagnatore della squadra.</p>
            </section>

            <section id="abbigliamento" className="regolamento-section">
              <h2>1.7 - Abbigliamento</h2>
              <p>L'abbigliamento di base obbligatorio di un giocatore è composto da:</p>
              <ul>
                <li>maglia a maniche corte o anche a maniche lunghe purché aderenti;</li>
                <li>pantaloncini senza passanti e senza tasche;</li>
                <li>calzettoni;</li>
                <li>scarpe leggere senza tacchetti superiori a 5 mm rigidi o aghi;</li>
                <li>scalpo;</li>
                <li>Il portiere ha l'obbligo di ginocchiere morbide;</li>
              </ul>
              <p>Ogni squadra deve avere due maglie, di colori diversi, di cui almeno una provvista di numero.</p>
              <p>Sono vietati:</p>
              <ul>
                <li>Cintura, gonne, kilt o simili</li>
                <li>Scarpe pesanti, scarponi, anfibi, scarpe con tacchetti superiori a 5mm o aghi;</li>
                <li>Uso di guanti o sostanze per aumentare la presa sulla palla;</li>
                <li>Protezioni (es ginocchiere) di plastica rigida, elmetti, parastinchi;</li>
                <li>Orecchini, collane, spille, bracciali, orologi;</li>
              </ul>
              <p>È diritto del capitano richiedere la verifica dell'abbigliamento della squadra avversaria. Qualora emergesse successivamente un'irregolarità nell'abbigliamento di un giocatore, la partita è comunque da considerarsi come regolarmente disputata.</p>
            </section>

            <section id="lo-scalpo" className="regolamento-section">
              <h2>1.8 - Lo scalpo</h2>
              <p>Lo scalpo è un fazzolettone scout arrotolato e piegato a metà. Ogni giocatore è provvisto di uno scalpo, che deve essere indossato dentro ai pantaloncini in posizione centrale, in modo che le due punte escano dal pantaloncino di almeno 20 cm. Lo scalpo, al momento del controllo iniziale, deve pendere verso il basso. Non sono ammessi scalpi rettangolari o ad una sola falda. Lo scalpo deve essere di un colore differente da quello dei pantaloni in modo da risultare visibile.</p>
            </section>

            <section id="tempi-regolamentari" className="regolamento-section">
              <h2>1.9 - Tempi regolamentari</h2>
              <p>L'incontro si compone di due tempi regolamentari da 15 minuti ciascuno. Il tempo può essere fermato dall'arbitro solo al fine di necessario a sistemare le linee di campo o i pali delle porte, soccorrere un giocatore infortunato, recuperare una palla lanciata molto lontano dal campo, consultare i capitani, risolvere eventuali situazioni disciplinari o per il tempo del time-out.</p>
            </section>

            <section id="tempi-supplementari" className="regolamento-section">
              <h2>1.10 - Tempi supplementari</h2>
              <p>Nelle partite ad eliminazione diretta, in caso di parità alla fine dei tempi regolamentari è previsto un primo tempo supplementare di 5 minuti con silver meta: si gioca per tutta la durata prevista, indipendentemente da eventuali mete segnate.</p>
              <p>In caso di ulteriore parità è previsto un secondo tempo supplementare di 5 minuti con golden meta: la prima squadra che segna una meta vince e la partita termina immediatamente</p>
              <p>In caso di ulteriore parità alla fine del secondo tempo supplementare vince la squadra che ha fatto, nell'ordine, il maggior numero di scalpi buoni, il minore numero di falli di squadra, il minor numero di scalpi vuoti. In caso di ulteriore parità si applica la regola decisa dagli organizzatori dell'incontro.</p>
              
              <p>I tempi supplementari sono un "terzo tempo", che comporta le seguenti conseguenze:</p>
              <ul>
                <li>I vuoti personali si azzerano, ogni giocatore ha 2 ulteriori vuoti per tutta la durata dei supplementari;</li>
                <li>i giocatori scalpati "buono" nel secondo tempo possono rientrare in campo. Ogni squadra inizia i supplementari con 7 giocatori;</li>
                <li>Tra 1° e 2° tempo supplementare non si azzerano i vuoti personali, i giocatori scalpati pieno non rientrano in campo.</li>
              </ul>
            </section>

            <section id="time-out" className="regolamento-section">
              <h2>1.11 - Time-out</h2>
              <p>Ad ogni tempo di gioco, ad entrambe le squadre, è concesso un time-out di 60 secondi. Il time out deve essere chiamato dal capitano e viene concesso dall'arbitro alla prima interruzione del gioco.</p>
            </section>

            <section id="tempo-di-recupero" className="regolamento-section">
              <h2>1.12 - Tempo di recupero</h2>
              <p>L'arbitro può, a sua discrezione, concedere del tempo di recupero alla fine di ogni tempo regolamentare, fino a un massimo di 5 minuti, dandone una indicativa comunicazione alle squadre poco prima della fine del tempo. Non è previsto tempo di recupero nei tempi supplementari.</p>
            </section>

            <section id="sostituzione-dei-giocatori-nel-corso-dellincontro" className="regolamento-section">
              <h2>1.13 - Sostituzione dei giocatori nel corso dell'incontro</h2>
              <p>Le sostituzioni sono illimitate, effettuabili a gioco fermo e solo da parte del capitano. La sostituzione stessa deve avvenire quando l'arbitro responsabile lo autorizza. La sostituzione del portiere o un cambio di ruolo dei giocatori col portiere va sempre autorizzato dall'arbitro a gioco fermo.</p>
            </section>
          </section>

          {/* 2 - SVOLGIMENTO DEL GIOCO */}
          <section id="svolgimento-del-gioco" className="regolamento-section">
            <h1>2 - SVOLGIMENTO DEL GIOCO</h1>

            <section id="cerchio-di-inizio-incontro" className="regolamento-section">
              <h2>2.1 - Cerchio di inizio incontro</h2>
              <p>Prima dell'inizio dell'incontro le due squadre si riuniscono in cerchio a centro campo insieme all'arbitro per un breve momento di conoscenza dell'arbitro, delle squadre, dei capitani e dei giocatori delle squadre. I capitani, i refertisti e i portieri si identificano all'arbitro.</p>
            </section>

            <section id="inizio-del-gioco" className="regolamento-section">
              <h2>2.2 - Inizio del gioco</h2>
              <p>L'arbitro assegna il primo possesso ad una squadra con un metodo definito (es "testa o croce") e all'altra squadra fa scegliere su che lato del campo iniziare l'incontro. Le squadre si dispongono sulle rispettive linee di fondo e l'arbitro fischia l'inizio dell'incontro.</p>
            </section>

            <section id="il-possesso-della-palla" className="regolamento-section">
              <h2>2.3 - Il possesso della palla</h2>
              <p>Ai fini del gioco viene considerato come possesso di palla quando un giocatore la controlla stabilmente o la colpisce a mano aperta, atto considerato come "possesso istantaneo". Non è invece consentito colpire la palla con il pugno, atto che deve essere sanzionato come fallo di corpo.</p>
              <p>Viene definito come "possesso di squadra" il tempo che una squadra controlla la palla. Si interrompe nel caso un giocatore avversario tocca la palla o il gioco si interrompe.</p>
            </section>

            <section id="palla-contesa" className="regolamento-section">
              <h2>2.4 - Palla contesa</h2>
              <p>Nel caso in cui la palla risulti contesa tra due giocatori senza che sia commesso fallo, l'arbitro fischia interrompe l'azione e gioco riprende con una palla a due tra i giocatori interessati, battuta sul punto della palla contesa o, se questo fosse all'interno di un'area, sulla linea della stessa. L'arbitro lascia cadere la palla tra i due giocatori e può essere recuperata dopo il suo primo rimbalzo a terra; gli altri giocatori devono tenersi ad una distanza di 2 m dal punto di rimessa.</p>
            </section>

            <section id="scalpo-vuoto-o-a-vuoto-o-non-buono" className="regolamento-section">
              <h2>2.5 - Scalpo "vuoto" o "a vuoto" o "non buono"</h2>
              <p>Lo scalpo è vuoto quando il giocatore viene scalpato e non è a contatto con la palla per tutta la durata dello scalpo.</p>
              <p>Il giocatore compie tre scalpi a vuoto deve abbandonare il campo di gioco e può rientrarvi solo all'inizio del successivo tempo di gioco. Il giocatore che abbandona il campo di gioco dopo il terzo scalpo a vuoto può essere sostituito da un altro giocatore disponibile solo dopo una meta fatto o subita o all'inizio del tempo successivo.</p>
              <p>La rimessa in gioco della palla è sempre effettuata da chi ha subìto lo scalpo a vuoto. Il computo degli scalpi a vuoto personali si azzera alla fine di ogni tempo di gioco.</p>
            </section>

            <section id="vuoti-di-squadra" className="regolamento-section">
              <h2>2.6 - Vuoti di squadra</h2>
              <p>Durante l'incontro viene il conteggio del numero degli scalpi vuoti commessi dai giocatori appartenenti alla stessa squadra. Tale conteggio non è mai azzerato per l'intero incontro.</p>
              <p>Al raggiungimento del 14° scalpo vuoto di squadra verrà assegnato un fallo di squadra. Un ulteriore fallo di squadra verrà assegnato ogni successivi tre scalpi vuoti dopo il 14° (quindi al 17º, 20º ecc.).</p>
            </section>

            <section id="scalpo-buono-o-pieno" className="regolamento-section">
              <h2>2.7 - Scalpo "buono" o "pieno"</h2>
              <p>Lo scalpo è buono quando:</p>
              <ul>
                <li>l'estrazione dello scalpo inizia e finisce quando il giocatore è in contatto con la palla;</li>
                <li>il giocatore lancia la palla in aria, viene scalpato, e successivamente viene a contatto con la palla al volo, anche accidentalmente, senza che vi siano stati nel frattempo rimbalzi o tocchi di altre superfici.</li>
              </ul>
              <p>Il giocatore che ha subìto lo scalpo buono, dopo avere effettuato la rimessa abbandona il campo e può rientrarvi solo dopo una meta della sua squadra o di quella avversaria, o nel caso questa non si verifichi entro la fine del tempo di gioco, all'inizio del successivo tempo di gioco.</p>
              <p>Non si può costringere il giocatore al contatto con la palla per poi scalparlo, né spingerlo sulla palla che ricade dopo che egli l'abbia lanciata in aria per evitare lo scalpo buono. Non è ammesso l'autoscalpo, né lo scalpo ai compagni di squadra.</p>
              <p>Dopo lo scalpo è buona norme ridare lo scalpo in mano al giocatore che l'ha subito.</p>
            </section>

            <section id="meta" className="regolamento-section">
              <h2>2.8 - Meta</h2>
              <p>È meta quando un giocatore appoggia, trascina o rotola tutta la palla a terra oltre la linea di porta avversaria, senza mai perdere il contatto della palla con una parte qualsiasi del proprio corpo dal momento in cui la palla oltrepassa il "muro laser".</p>
              <p>Il giocatore deve sempre mantenere la propria posizione in meta e trattenere la palla in posizione fino a decisione arbitrale avvenuta, con l'esternazione della decisione arbitrale; se il giocatore dovesse spostarsi senza che l'arbitro abbia potuto sincerarsi della validità della meta, quest'ultima potrà essere considerata non valida.</p>
              <p>L'arbitro fischia quando la palla è appoggiata o portata oltre la linea di porta, tale fischio non è da considerarsi convalida della meta ma ha il solo scopo di interrompere il gioco per valutare la validità della stessa.</p>
              <p>La meta non è valida quando:</p>
              <ul>
                <li>il giocatore supera la verticale della linea di porta con una parte qualsiasi del piede, anche dopo aver appoggiato la palla;</li>
                <li>il giocatore non riesce a trattenere la palla in posizione fino alla convalida dell'arbitro, ad esclusione di casi di fallo;</li>
                <li>il giocatore prende la palla dopo che questa ha già superato la linea di porta, ossia in fallo di fondo;</li>
                <li>Non è ammessa l'autometa.</li>
              </ul>
            </section>

            <section id="ripresa-del-gioco-dopo-una-meta" className="regolamento-section">
              <h2>2.9 - Ripresa del gioco dopo una meta</h2>
              <p>Dopo una meta convalidata, le squadre tornano nella propria area. L'arbitro prende la palla e la consegna, con un fischio che ne sancisce la rimessa in gioco, alla squadra che ha subito meta.</p>
            </section>

            <section id="meta-tecnica" className="regolamento-section">
              <h2>2.10 - Meta tecnica</h2>
              <p>È "meta tecnica" quando rimangono in campo solo tre giocatori di una squadra. Ai fini del referto, il giocatore che ha effettuato l'ultimo scalpo viene considerato anche colui che ha segnato la meta.</p>
            </section>

            <section id="il-portiere" className="regolamento-section">
              <h2>2.11 - Il portiere</h2>
              <p>Il portiere è un giocatore con caratteristiche particolari, dedicato alla difesa della propria porta. Il portiere è l'unico giocatore che può sempre rimanere nell'area di difesa della propria squadra e l'unico che può, esclusivamente nella propria metà campo, privare l'avversario della palla, intervenendo però solo su di essa.</p>
              <p>Il portiere non è da considerarsi fuori se il portiere interviene con un piede oltre la linea della propria porta. Nel caso esca con entrambi i piedi, invece, deve comunque fare il giro dai conetti laterali qualora la palla rimanga in gioco. Fuori dallo specchio della porta il portiere è soggetto alle stesse regole del fallo di fondo degli altri giocatori.</p>
              <p>Il portiere che, abusando della sua prerogativa di poter intervenire sulla palla, mette a rischio la salute dei giocatori avversari è sanzionato secondo il fallo di corpo. Non è possibile giocare senza portiere in campo.</p>
            </section>

            <section id="rimessa-dopo-fallo-o-scalpo" className="regolamento-section">
              <h2>2.12 - Rimessa dopo fallo o scalpo</h2>
              <p>La rimessa in seguito ad un fallo o ad uno scalpo viene effettuata dall'esterno del campo, all'altezza del punto in cui è stato commesso il fallo o lo scalpo, salvo diversamente indicato nelle singole regole. La rimessa è eseguita dal giocatore che ha subìto il fallo o lo scalpo, o da un giocatore qualsiasi nel caso di un fallo non subìto da un singolo giocatore, salvo diversamente indicato nelle singole regole.</p>
              <p>L'arbitro fischia la rimessa non appena il giocatore incaricato è in posizione ed ha eventualmente risistemato scalpo e abbigliamento; il giocatore che effettua la rimessa ha 5 secondi di tempo dal fischio per rimettere la palla in gioco.</p>
              <p>Il superamento dei 5 secondi, la rimessa da posizione irregolare, la perdita di tempo in caso di situazione di vantaggio, la rimessa prima del fischio dell'arbitro sono sanzionate con un cambio rimessa a favore della squadra avversaria.</p>
              <p>Il giocatore che effettua la rimessa non può essere scalpato, ma solo ostacolato dagli avversari in campo, e può indietreggiare a piacere dalla linea per effettuare la rimessa più agevolmente.</p>
              <p>Il giocatore che rimette la palla in gioco può rientrarne in possesso solo dopo che la palla ha colpito, anche accidentalmente, un altro giocatore.</p>
              <p>La palla è da considerare "in gioco" dal momento che lascia la mano del giocatore che rimette.</p>
            </section>
          </section>

          {/* 3 - FALLI E SANZIONI */}
          <section id="falli-e-sanzioni" className="regolamento-section">
            <h1>3 - FALLI E SANZIONI</h1>

            <section id="fallo-laterale-e-fallo-di-fondo" className="regolamento-section">
              <h2>3.1 - Fallo laterale e fallo di fondo</h2>
              <p>La palla è da considerarsi fuori quando:</p>
              <ul>
                <li>Rimbalza fuori dal campo o sulla linea laterale o di fondo</li>
                <li>Il giocatore in contatto con la palla tocca con una qualsiasi parte del corpo la linea laterale o di fondo o fuori</li>
                <li>La palla oltrepassa totalmente la linea di porta, all'interno dei pali, senza essere a contatto con un giocatore</li>
              </ul>
              <p>L'arbitro fischia un fallo laterale o di fondo, a seconda della linea dove viene commesso, e assegna la rimessa alla squadra avversaria.</p>
              <p>La rimessa viene effettuata:</p>
              <ul>
                <li>dal lato nel punto esatto dove è uscita la palla in caso di fallo laterale;</li>
                <li>dall'angolo in caso di fallo di fondo commesso dalla squadra in difesa;</li>
                <li>da oltre la linea di fondo, in caso di fallo di fondo commesso dalla squadra in attacco.</li>
              </ul>
              <p>La rimessa non necessità del fischio dell'arbitro.</p>
            </section>

            <section id="fallo-di-corpo" className="regolamento-section">
              <h2>3.2 - Fallo di corpo</h2>
              <p>Un giocatore che ostacola un avversario trattenendolo, colpendolo, spingendolo, ostruendolo col corpo, caricandolo, sgambettandolo, o impedendogli di avanzare estendendo la sua mano, braccio, gomito, spalla, fianco, gamba, ginocchio o piede, o piegando il suo corpo in una posizione "non naturale" o praticando un gioco duro o violento commette fallo; l'arbitro fischia un fallo di corpo ed assegna la rimessa al giocatore che ha subìto il fallo.</p>
            </section>

            <section id="fallo-di-sfondamento" className="regolamento-section">
              <h2>3.3 - Fallo di sfondamento</h2>
              <p>Il giocatore che urta un avversario fermo in posizione legale di difesa, ossia con entrambi i piedi a terra nel momento del contatto, commette fallo; l'arbitro fischia un fallo di sfondamento, ed assegna la rimessa al giocatore che ha subìto il fallo.</p>
              <p>Allo stesso modo il difensore deve stabilire la propria posizione di difesa in maniera regolare, senza quindi causare contatti fallosi prima di averla assunta.</p>
              <p>L'attaccante deve aspettarsi d'essere marcato, anche molto da vicino, e quindi deve essere pronto ad arrestarsi o a cambiare direzione tutte le volte che un avversario gli si pone davanti in maniera legale, anche all'ultimo momento.</p>
              <p>Il fallo di sfondamento è sempre punito con un vuoto personale.</p>
            </section>

            <section id="fallo-di-blocco" className="regolamento-section">
              <h2>3.4 - Fallo di blocco</h2>
              <p>Il giocatore che avvantaggia un compagno impedendo con il proprio corpo a un giocatore avversario di avanzare liberamente effettua un "blocco", commettendo quindi fallo. È fallo anche se avviene un contatto con il braccio tenuto rigido che ostacola di fatto il movimento dell'attaccante.</p>
            </section>

            <section id="fallo-di-piede" className="regolamento-section">
              <h2>3.5 - Fallo di piede</h2>
              <p>Un giocatore che colpisce la palla con il proprio corpo dal ginocchio in giù, commette fallo.</p>
            </section>

            <section id="fallo-di-palleggio" className="regolamento-section">
              <h2>3.6 - Fallo di palleggio</h2>
              <p>Un giocatore non può spingere volontariamente la palla verso il suolo, ottenendo un rimbalzo che fa tornare la palla nella sua mano in continuità e senza interruzione, per più di una volta durante il suo possesso di palla.</p>
            </section>

            <section id="fallo-di-scalpo-preparato-o-manina-o-mano-sullo-scalpo" className="regolamento-section">
              <h2>3.7 - Fallo di scalpo preparato o "manina" o "mano sullo scalpo"</h2>
              <p>Il giocatore che scalpa l'avversario in possesso di palla, avendo mantenuto la mano in prossimità dello scalpo dell'avversario senza palla, anche se entrambi i giocatori sono in movimento, commette fallo.</p>
              <p>Il fallo è sempre sanzionato con uno scalpo vuoto</p>
            </section>

            <section id="fallo-di-palo" className="regolamento-section">
              <h2>3.8 - Fallo di palo</h2>
              <p>Se un attaccante urta il palo facendolo inclinare con un angolo minore di 45° rispetto al terreno commette fallo.</p>
              <p>Se l'abbattimento del palo è causato da un membro della squadra in difesa durante la concretizzazione dell'azione, l'arbitro può assegnare il vantaggio alla squadra in attacco.</p>
            </section>

            <section id="fallo-di-protezione" className="regolamento-section">
              <h2>3.9 - Fallo di protezione</h2>
              <p>Il giocatore in possesso di palla che impedisce all'avversario di togliergli lo scalpo, proteggendo quest'ultimo con le braccia o con la palla stessa, commette fallo.</p>
            </section>

            <section id="fallo-di-vestiario" className="regolamento-section">
              <h2>3.10 - Fallo di vestiario</h2>
              <p>Il giocatore che partecipa in un qualunque modo all'azione non è in regola con l'abbigliamento commette fallo.</p>
              <p>Se è un avversario a causare tale fallo, in questo caso verrà assegnato il vantaggio o la rimessa dal lato per il giocatore non in regola.</p>
            </section>

            <section id="fallo-di-posizione-in-attacco" className="regolamento-section">
              <h2>3.11 - Fallo di posizione in attacco</h2>
              <p>I giocatori della squadra attaccante possono stare nell'area avversaria indefinitivamente se la palla è all'interno dell'area, o per massimo 4 secondi se la palla è al di fuori.</p>
              <p>Il conteggio dei 4 secondi è personale e riferito ad ogni singolo giocatore, inizia e si esaurisce in base alla posizione di ogni singolo giocatore e della palla. L'ingresso in area della palla fa azzerare il conteggio dei 4 secondi, che ricomincia nel momento in cui la palla esce dall'area.</p>
            </section>

            <section id="fallo-di-posizione-in-difesa" className="regolamento-section">
              <h2>3.12 - Fallo di posizione in difesa</h2>
              <p>I giocatori possono trovarsi nella propria area di difesa se, e solo se:</p>
              <ul>
                <li>la palla è all'interno dell'area;</li>
                <li>almeno un giocatore della squadra avversaria è all'interno dell'area.</li>
              </ul>
              <p>Nel momento in cui un giocatore si trovi nella propria area senza che sia presente la palla oppure uno o più giocatori della squadra avversaria commette fallo.</p>
              <p>Il fallo è sempre sanzionato con un fallo di squadra</p>
            </section>

            <section id="fallo-di-melina" className="regolamento-section">
              <h2>3.13 - Fallo di melina</h2>
              <p>La squadra in vantaggio e in possesso palla che perde tempo nella propria metà campo commette fallo. È considerato fallo di squadra.</p>
            </section>

            <section id="fallo-campo" className="regolamento-section">
              <h2>3.14 - Fallo campo</h2>
              <p>La squadra in possesso che porta la palla nella metà campo avversaria non può mai riportare la palla, o il giocatore in possesso, nella propria metà campo. Qualora succeda l'arbitro fischia un fallo e assegna la rimessa alla squadra avversaria che effettua la rimessa da metà campo.</p>
            </section>

            <section id="regola-del-vantaggio" className="regolamento-section">
              <h2>3.15 - Regola del vantaggio</h2>
              <p>La regola del vantaggio viene applicata nei seguenti casi e modi:</p>
              <ul>
                <li>quando viene scalpato a vuoto o con scalpo preparato il giocatore che effettua l'ultimo passaggio prima della meta, solo se questo passaggio porta nell'immediatezza il giocatore che lo riceve in meta. Se il vantaggio non si concretizza con la meta, il gioco riprende con la rimessa sullo scalpo che ha generato il vantaggio. Se il vantaggio porta alla meta il giocatore che ha commesso lo scalpo non subisce alcuna sanzione;</li>
                <li>quando in presenza di un fallo, l'arbitro ritiene che interrompendo il gioco risulterebbe avvantaggiata la squadra che ha commesso l'infrazione stessa. In questo caso si continua a giocare come se il fallo non fosse mai accaduto.</li>
              </ul>
            </section>

            <section id="uscita-dal-fondo-di-un-giocatore" className="regolamento-section">
              <h2>3.16 - Uscita dal fondo di un giocatore</h2>
              <p>Un giocatore che nel corso di un'azione si trovasse ad uscire dal fondo del campo dovrà rientrare in campo dalle linee laterali fuori dalla zona d'area (conetti). In caso il giocatore rientri in campo direttamente dal fondo commette fallo.</p>
            </section>

            <section id="fallo-chiara-occasione-da-meta" className="regolamento-section">
              <h2>3.17 - Fallo "chiara occasione da meta"</h2>
              <p>Un "fallo da chiara occasione da meta" è un fallo che avviene sul portatore di palla in procinto di fare meta da parte di un giocatore della squadra avversaria.</p>
              <p>In questo caso l'arbitro, oltre alla sanzione personale al giocatore che commette il fallo, assegna sempre due "falli di squadra" alla squadra.</p>
            </section>

            <section id="falli-di-squadra" className="regolamento-section">
              <h2>3.18 - Falli di squadra</h2>
              <p>I falli di squadra sono comminati all'intera squadra indipendentemente dal giocatore che li ha commessi.</p>
              <p>Tre falli di squadra nello stesso incontro corrispondono ad una meta subita.</p>
              <p>Sono considerati falli di squadra:</p>
              <ul>
                <li>il fallo di difesa;</li>
                <li>il fallo di melina;</li>
                <li>sostituzioni o cambi di ruolo a gioco attivo o senza consenso dell'arbitro responsabile;</li>
                <li>errori nel numero e sesso dei giocatori in campo;</li>
                <li>conteggio dei vuoti di squadra;</li>
                <li>fallo da chiara occasione da meta.</li>
              </ul>
            </section>

            <section id="infrazioni-sanzionabili-con-uno-scalpo-a-vuoto" className="regolamento-section">
              <h2>3.19 - Infrazioni sanzionabili con uno scalpo a vuoto</h2>
              <p>Lo scalpo a vuoto può essere assegnato, a discrezione dell'arbitro oltre ai casi già previsti, quando un giocatore:</p>
              <ul>
                <li>commette in maniera reiterata lo stesso fallo;</li>
                <li>usa violenza verbale (insulti, imprecazioni ecc);</li>
                <li>si rivolge all'arbitro con parole e gesti suggerendo in maniera insistente la decisione o contestandola;</li>
                <li>discute animatamente con un giocatore della squadra avversaria in merito alle decisioni arbitrali;</li>
                <li>commette un fallo tattico;</li>
                <li>commette un fallo potenzialmente pericoloso;</li>
              </ul>
            </section>

            <section id="infrazioni-sanzionabili-con-due-scalpi-a-vuoto" className="regolamento-section">
              <h2>3.20 - Infrazioni sanzionabili con due scalpi a vuoto</h2>
              <p>Un giocatore deve essere sanzionato con due scalpi a vuoto nei seguenti casi:</p>
              <ul>
                <li>commette fallo antisportivo e/o intenzionale;</li>
                <li>commette falli pericolosi;</li>
                <li>colpisce la palla con il pugno mettendo così in pericolo la salute dell'avversario;</li>
              </ul>
            </section>

            <section id="infrazioni-passibili-di-ammonizione-giallo" className="regolamento-section">
              <h2>3.21 - Infrazioni passibili di ammonizione (giallo)</h2>
              <p>Un giocatore può essere ammonito, mostrandogli il cartellino giallo, quando commette una delle seguenti infrazioni:</p>
              <ul>
                <li>manifesta disapprovazione con parole o gesti verso una decisione dell'arbitro, rivolgendosi a questi in maniera irrispettosa;</li>
                <li>è colpevole di un comportamento antisportivo o contrario allo spirito del gioco;</li>
              </ul>
              <p>L'ammonizione comporta l'esclusione del giocatore per tutto il tempo di gioco. Il giocatore ammonito abbandona il campo di gioco e può essere sostituito da un altro giocatore disponibile solo dopo una meta o all'inizio del tempo successivo.</p>
            </section>

            <section id="infrazioni-passibili-di-espulsione-rosso" className="regolamento-section">
              <h2>3.22 - Infrazioni passibili di espulsione (rosso)</h2>
              <p>Un giocatore può essere espulso, mostrandogli il cartellino rosso, quando commette una delle seguenti infrazioni:</p>
              <ul>
                <li>riceve una seconda ammonizione nello stesso incontro;</li>
                <li>usa un linguaggio o gesti offensivi o minacciosi nei confronti dell'arbitro o degli avversari;</li>
                <li>è colpevole di una condotta volontariamente violenta;</li>
              </ul>
              <p>L'espulsione comporta l'esclusione del giocatore per tutto il resto dell'incontro. Il giocatore espulso abbandona il campo di gioco e può essere sostituito da un altro giocatore disponibile solo dopo una meta o all'inizio del tempo successivo.</p>
            </section>

            <section id="infrazioni-passibili-di-espulsione-nero" className="regolamento-section">
              <h2>3.23 - Infrazioni passibili di espulsione (nero)</h2>
              <p>Un giocatore può essere espulso dall'intera competizione mostrandogli il cartellino nero, quando commette una delle seguenti infrazioni:</p>
              <ul>
                <li>Bestemmia;</li>
                <li>Infierisce fisicamente o sputa su un avversario;</li>
                <li>Cerca ripetutamente lo scontro fisico con un giocatore avversario;</li>
              </ul>
              <p>L'espulsione comporta l'esclusione del giocatore per tutto il resto dell'incontro. Il giocatore espulso abbandona il campo di gioco e può essere sostituito da un altro giocatore disponibile solo dopo una meta, o all'inizio del tempo successivo.</p>
              <p>Sulle conseguenze e sulla gravità delle espulsioni decide l'organizzazione del torneo.</p>
            </section>

            <section id="infrazioni-passibili-di-espulsione-nero" className="regolamento-section">
              <h2>3.23 - Infrazioni passibili di espulsione (nero)</h2>
              <p>Un giocatore può essere espulso dall'intera competizione mostrandogli il cartellino nero, quando commette una delle seguenti infrazioni:</p>
              <ul>
                <li>Bestemmia;</li>
                <li>Infierisce fisicamente o sputa su un avversario;</li>
                <li>Cerca ripetutamente lo scontro fisico con un giocatore avversario;</li>
              </ul>
              <p>L'espulsione comporta l'esclusione del giocatore per tutto il resto dell'incontro. Il giocatore espulso abbandona il campo di gioco e può essere sostituito da un altro giocatore disponibile solo dopo una meta, o all'inizio del tempo successivo.</p>
              <p>Sulle conseguenze e sulla gravità delle espulsioni decide l'organizzazione del torneo.</p>
            </section>
          </section>

          {/* Footer del documento */}
          <footer className="regolamento-footer">
            <p>Regolamento Scoutball 2025</p>
            <p>Pattuglia Scoutball - A.G.E.S.C.I. Zona di Bologna</p>
          </footer>
        </main>
      </div>

      {/* Bottone scroll to top */}
      <button
        className={`regolamento-scroll-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Torna in cima"
      >
        <i className="fas fa-arrow-up"></i>
      </button>

      <Footer />
    </div>
  );
};

export default Regolamento;